import { useMemo } from 'react'

import type { ResponseDsCompletionRate } from '@/schema'
import {
  AnalyticsLevel,
  ResolutionRate,
  ResponseAvgCycleTime,
  ResponseCommentDensity,
  ResponseLateDeliverables,
  SuccessResponseList,
} from '@/schema'

import {
  useGetAvgCycleTime,
  useGetCommentDensity,
  useGetDsCompletionRate,
  useGetLateDeliverables,
  useGetResolutionMetrics,
} from './index'
import { useProjectLevelShared } from './useProjectLevelShared'

type TUseProjectLevelResult = {
  commentDensityMetric: ResponseCommentDensity
  lateDeliverablesMetric: ResponseLateDeliverables
  avgCycleTimeMetric: ResponseAvgCycleTime
  dsCompletionRateMetric?: SuccessResponseList<ResponseDsCompletionRate>
  resolutionMetric?: ResolutionRate
  resolutionRate?: number
  openCommentsCount?: number
  approvedDeliverableSets: number
  totalDeliverableSets: number
  isLoadingCommentDensity: boolean
  isLoadingLateDeliverables: boolean
  isLoadingResolutionMetrics: boolean
  isLoadingAvgCycleTime: boolean
  isLoadingDsCompletionRate: boolean
}

export const useProjectLevel = (): TUseProjectLevelResult => {
  const { queryOptions, analyticsParams, projectParams } =
    useProjectLevelShared()

  const {
    data: { data: [lateDeliverablesMetric] = [] } = {},
    isLoading: isLoadingLateDeliverables,
  } = useGetLateDeliverables(analyticsParams, queryOptions)

  const {
    data: { data: [commentDensityMetric] = [] } = {},
    isLoading: isLoadingCommentDensity,
  } = useGetCommentDensity(analyticsParams, queryOptions)

  const {
    data: { data: [resolutionMetricsResponse] = [] } = {},
    isLoading: isLoadingResolutionMetrics,
  } = useGetResolutionMetrics(
    {
      level: AnalyticsLevel.project,
      project_id: analyticsParams.project_id,
    },
    queryOptions
  )

  const {
    data: { data: [avgCycleTimeResponse] = [] } = {},
    isLoading: isLoadingAvgCycleTime,
  } = useGetAvgCycleTime(projectParams, queryOptions)

  const { data: dsCompletionRateMetric, isLoading: isLoadingDsCompletionRate } =
    useGetDsCompletionRate(projectParams, queryOptions)

  const resolutionMetric = useMemo(() => {
    return resolutionMetricsResponse?.data?.[0]
  }, [resolutionMetricsResponse])

  const resolutionRate = useMemo(() => {
    return resolutionMetricsResponse?.metric
  }, [resolutionMetricsResponse])

  const openCommentsCount = useMemo(() => {
    return resolutionMetric?.open_count
  }, [resolutionMetric])

  const approvedDeliverableSets = useMemo(() => {
    return dsCompletionRateMetric?.data?.[0]?.data?.[0]?.approved_count ?? 0
  }, [dsCompletionRateMetric])

  const totalDeliverableSets = useMemo(() => {
    return dsCompletionRateMetric?.data?.[0]?.data?.[0]?.total_count ?? 0
  }, [dsCompletionRateMetric])

  return useMemo(
    () => ({
      lateDeliverablesMetric,
      isLoadingLateDeliverables,
      commentDensityMetric,
      isLoadingCommentDensity,
      resolutionMetric,
      resolutionRate,
      openCommentsCount,
      isLoadingResolutionMetrics,
      avgCycleTimeMetric: avgCycleTimeResponse,
      isLoadingAvgCycleTime,
      dsCompletionRateMetric,
      isLoadingDsCompletionRate,
      approvedDeliverableSets,
      totalDeliverableSets,
    }),
    [
      lateDeliverablesMetric,
      isLoadingLateDeliverables,
      commentDensityMetric,
      isLoadingCommentDensity,
      resolutionMetric,
      resolutionRate,
      openCommentsCount,
      isLoadingResolutionMetrics,
      avgCycleTimeResponse,
      isLoadingAvgCycleTime,
      dsCompletionRateMetric,
      isLoadingDsCompletionRate,
      approvedDeliverableSets,
      totalDeliverableSets,
    ]
  )
}
